import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Row,
	Col,
	Select,
	notification,
	DatePicker,
	Tag,
} from "antd";
import Layout from "../layout/Layout";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import dayjs from "dayjs";
import JobLoadsRateModal from "./JobLoadsRateModal";
const { Text } = Typography;
const { RangePicker } = DatePicker;

const Logistics57Dashboard = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(true);
	const [jobList, setJobList] = useState([]);
	const [sellerList, setSellerList] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [plant, setPlant] = useState("All");
	const [seller, setSeller] = useState("All");
	const [plantList, setPlantList] = useState([]);
	const [selectedJob, setSelectedJob] = useState(null);
	const [modalOpenLoads, setModalOpenLoads] = useState(false);

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Job ID",
			dataIndex: "jobId",
			key: "jobId",
			render: (value, record) => {
				return (
					<Text
						underline
						strong
						style={{ cursor: "pointer" }}
						onClick={() => {
							setSelectedJob(record);
							setModalOpenLoads(true);
						}}
					>
						{value}
					</Text>
				);
			},
		},
		{
			title: "Customer",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Target YD3",
			dataIndex: "targetYD3",
			key: "targetYD3",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Loaded YD3",
			dataIndex: "loadYD3",
			key: "loadYD3",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Scheduled Time",
			dataIndex: "scheduledTime",
			key: "scheduledTime",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Rate / CY",
			dataIndex: "rateCY",
			key: "rateCY",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "On Time Threshold",
			dataIndex: "performanceOnTime",
			key: "performanceOnTime",
			render: (value, record) => {
				return (
					<Flex>
						<Tag
							color={
								value === "Pass"
									? "green"
									: value === "Failed"
										? "red"
										: "default"
							}
						>
							{value}
						</Tag>
						{record.averageOfTime && <Text>{record.averageOfTime}%</Text>}
					</Flex>
				);
			},
		},
		{
			title: "Franchise",
			dataIndex: "franchise",
			key: "franchise",
			render: (value) => {
				return (
					<Tag
						color={
							value === "57Concrete"
								? "blue"
								: value === "Castle"
									? "green"
									: "cyan"
						}
					>
						{value}
					</Tag>
				);
			},
		},
	];

	useEffect(() => {
		getJobs();
	}, [seller, plant, startDate, endDate]);

	useEffect(() => {
		getPlants();
		getSellerList();
	}, []);

	const getJobs = async () => {
		try {
			setLoading(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/57logistics/${seller}/${plant}?startDate=${start}&endDate=${end}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);

			if (data.success) {
				let list = [];
				data.payload.map((job) => {
					list.push({
						id: job._id,
						key: job._id,
						index: job._id,
						jobId: job.number,
						franchise: job.franchise || "57Concrete",
						customer: job.customer.name,
						seller: job.seller.name,
						loadYD3: job.loadYD3,
						targetYD3: job.targetYD3,
						plant: job.plant.name,
						scheduledTime: dayjs(job.scheduledDate).format(
							"MM/DD/YYYY HH:mm A",
						),
						number: job.number,
						deliveryRate: job.deliveryRate,
						rateCY: job.deliveryRate || "-",
						averageOfTime: job.averageOfTime,
						performanceOnTime:
							job.deliveryRate && job.averageOfTime
								? job.averageOfTime >= 80
									? "Pass"
									: "Failed"
								: "Failed",
					});
				});
				setJobList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
			setLoadingTable(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				let list = [
					{
						value: "All",
						label: "All",
					},
				];
				data.payload
					.filter((i) => i.name !== "Laredo")
					.map((item) => {
						list.push({
							value: item._id,
							label: item.name,
						});
					});
				setPlantList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getSellerList = async () => {
		try {
			const url = api + "/user/list/57Concrete/Seller";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [{ value: "All", label: "All" }];
				data.payload.map((item, index) => {
					list.push({
						value: item._id,
						label: item.name,
					});
				});
				setSellerList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const onChangePlantPicker = (value) => {
		setPlant(value);
	};
	const onChangeSellerPicker = (value) => {
		setSeller(value);
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const RenderDates = () => {
		return (
			<Flex gap={2}>
				<Row>
					<Col span={24}>
						<Text>Dates</Text>
					</Col>
					<Col span={24}>
						<RangePicker
							defaultValue={[
								dayjs(startDate, "MM/DD/YYYY"),
								dayjs(endDate, "MM/DD/YYYY"),
							]}
							onChange={onRangeChange}
							format={"MM/DD/YYYY"}
							size="large"
							disabled={loading}
							allowClear={false}
						/>
					</Col>
				</Row>

				<Row>
					<Col span={24}>
						<Text>Plant</Text>
					</Col>
					<Col span={24}>
						<Select
							showSearch
							placeholder={`Select plant`}
							optionFilterProp="children"
							size="large"
							onChange={onChangePlantPicker}
							filterOption={filterOption}
							options={plantList}
							disabled={loading}
							value={plant}
							style={{
								width: "250px",
							}}
							allowClear={false}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						<Text>Seller</Text>
					</Col>
					<Col span={24}>
						<Select
							showSearch
							placeholder={`Select seller`}
							optionFilterProp="children"
							size="large"
							onChange={onChangeSellerPicker}
							filterOption={filterOption}
							options={sellerList}
							disabled={loading}
							value={seller}
							style={{
								width: "250px",
							}}
							allowClear={false}
						/>
					</Col>
				</Row>
			</Flex>
		);
	};

	const RenderTable = () => {
		return (
			<CustomTable
				columns={columns}
				data={jobList}
				loading={loadingTable}
				isScroll={true}
			/>
		);
	};

	return (
		<Layout title="Logistics Dashboard" description="Dashboard">
			<Flex vertical gap={"large"}>
				<RenderDates />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
			</Flex>
			<JobLoadsRateModal
				modalOpenLoads={modalOpenLoads}
				setModalOpenLoads={setModalOpenLoads}
				selectedJob={selectedJob}
				setSelectedJob={setSelectedJob}
				history={history}
			/>
		</Layout>
	);
};

export default Logistics57Dashboard;
